import React from 'react';
import {
	FontAwesomeIcon,
	type FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import type {IconProp} from '@fortawesome/fontawesome-svg-core';
import {
	Button as MuiButton,
	type ButtonProps as MuiButtonProps,
} from '@mui/material';
export {buttonClasses} from '@mui/material/Button';

export type ButtonProps<
	RootComponent extends React.ElementType<any> = 'button',
> = Omit<MuiButtonProps<RootComponent>, 'endIcon' | 'startIcon'> & {
	endIcon?: IconProp;
	startIcon?: IconProp;
	EndIconProps?: Omit<Partial<FontAwesomeIconProps>, 'icon'> & {
		'data-testid'?: string;
	};
	StartIconProps?: Omit<Partial<FontAwesomeIconProps>, 'icon'> & {
		'data-testid'?: string;
	};
};

export const ButtonPrimitive = <
	RootComponent extends React.ElementType<any> = 'button',
>(
	{
		children,
		endIcon,
		startIcon,
		EndIconProps,
		StartIconProps,
		...props
	}: ButtonProps<RootComponent>,
	ref: React.ForwardedRef<HTMLButtonElement>,
) => {
	return (
		<MuiButton<RootComponent>
			{...props}
			ref={ref}
			endIcon={
				endIcon && (
					<FontAwesomeIcon
						data-testid="button-end-icon"
						fixedWidth
						{...EndIconProps}
						icon={endIcon}
					/>
				)
			}
			startIcon={
				startIcon && (
					<FontAwesomeIcon
						data-testid="button-start-icon"
						fixedWidth
						{...StartIconProps}
						icon={startIcon}
					/>
				)
			}
		>
			{typeof children === 'string' ? (
				<span data-testid="action-button-label">{children}</span>
			) : (
				children
			)}
		</MuiButton>
	);
};

export const Button = React.forwardRef(ButtonPrimitive) as <
	RootComponent extends React.ElementType<any> = 'button',
>(
	props: ButtonProps<RootComponent>,
) => JSX.Element;

export default Button;
